import { CloseOutlined, InfoOutlined } from '@mui/icons-material';
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  Step,
  StepConnector,
  StepLabel,
  Stepper,
  TextField,
  Typography,
  useTheme
} from '@mui/material';
import { RestApiKey } from 'graphql/generated/graphqlTypes';
import useEnqueueSnackbar from 'hooks/useEnqueueSnackbar';
import useRestApiKey from 'hooks/useRestApiKey/useRestApiKey';
import useTranslation from 'hooks/useTranslation/useTranslation';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import ComponentWrapper from 'ui/templates/ComponentWrapper/ComponentWrapper';
import { API_KEY_MAX_NAME_LENGTH } from 'v2/constants';

interface Props {
  handleClose: () => void;
}

const StyledStepConnector = styled(StepConnector)`
  & span {
    height: 100%;
  }
`;

const AddApiKey: React.FC<React.PropsWithChildren<Props>> = ({ handleClose }) => {
  const theme = useTheme();
  const { translation } = useTranslation();
  const [activeStep, setActiveStep] = useState(0);
  const [apiKeyName, setApiKeyName] = useState<string | undefined>(undefined);
  const NAME_INPUT_STEP = 0;
  const LOADER_STEP = 1;
  const API_KEY_STEP = 2;
  const enqueueSnackbar = useEnqueueSnackbar();
  const handleFetchError = (errorMessage: string) => enqueueSnackbar(errorMessage, { snackbartype: 'error' });
  const { addRestApiKey, addRestApiKeyLoading } = useRestApiKey({ handleFetchError });
  const [addedApiKey, setAddedApiKey] = useState<RestApiKey>();

  const handleGenerateKey = async () => {
    if (!addRestApiKeyLoading)
      try {
        const response = await addRestApiKey(apiKeyName || '');
        if (response) setAddedApiKey(response.addRestApiKey);
      } finally {
        setActiveStep(LOADER_STEP);
      }
  };

  useEffect(() => {
    if (addRestApiKeyLoading) setActiveStep(LOADER_STEP);
  }, [addRestApiKeyLoading]);

  useEffect(() => {
    if (addedApiKey) setActiveStep(API_KEY_STEP);
  }, [addedApiKey]);

  const steps = [translation.api_details, translation.generate_key, translation.finish];
  const isApiKeyNameCorrect = Boolean(apiKeyName && apiKeyName?.length > API_KEY_MAX_NAME_LENGTH);

  const FirstStep = (
    <ComponentWrapper padding="1.5rem 1rem 1rem 1rem" width="25rem">
      <TextField
        onChange={(event) => setApiKeyName(event.target.value)}
        variant="outlined"
        fullWidth
        InputLabelProps={{ shrink: !!apiKeyName || undefined }}
        size="medium"
        label={translation.api_key_name}
        value={apiKeyName}
        id="input-name"
        error={isApiKeyNameCorrect}
        helperText={
          isApiKeyNameCorrect
            ? translation.name_cannot_be_longer_then_48_characters_please_shorten_it_and_try_again
            : undefined
        }
      />
    </ComponentWrapper>
  );

  const SecondStep = (
    <ComponentWrapper
      padding="1.5rem 1rem 1rem 1rem"
      width="25rem"
      justifyContent="center"
      alignItems="center"
      gap="1.5rem"
      flexDirection="column"
    >
      <CircularProgress size={32} />
      <Typography variant="h6" color="textPrimary">
        {translation.generate_key}
      </Typography>
    </ComponentWrapper>
  );

  const handleCopy = () => {
    if (addedApiKey) navigator.clipboard.writeText(addedApiKey.apiKey);
  };

  const ThirdStep = (
    <ComponentWrapper
      padding="1.5rem 1rem 1rem 1rem"
      width="25rem"
      justifyContent="center"
      alignItems="center"
      gap="1.5rem"
      flexDirection="column"
    >
      <TextField
        variant="outlined"
        fullWidth
        size="medium"
        label={translation.api_key}
        value={addedApiKey?.apiKey}
        id="input-api-key"
        disabled
        InputProps={{
          style: {
            fontFamily: 'Courier New',
            fontWeight: 400,
            fontSize: '16px',
            lineHeight: '24px',
            letterSpacing: '0.15px'
          },
          multiline: true
        }}
      />
      <ComponentWrapper gap="0.5rem">
        <InfoOutlined style={{ color: theme.palette.primary.main, fill: theme.palette.primary.main }} />
        <Typography color="textSecondary" variant="body2">
          {
            translation.this_is_your_unique_key_and_it_is_non_recoverable_if_you_lose_this_api_key_you_will_have_to_create_a_new_one
          }
        </Typography>
      </ComponentWrapper>
      <Button
        fullWidth
        variant="contained"
        size="large"
        color="secondary"
        onClick={handleCopy}
        style={{ color: theme.palette.text.primary }}
      >
        {translation.copy_key_to_clipboard}
      </Button>
    </ComponentWrapper>
  );

  const getStepContent = () => {
    switch (activeStep) {
      case LOADER_STEP:
        return SecondStep;
      case API_KEY_STEP:
        return ThirdStep;
      case NAME_INPUT_STEP:
      default:
        return FirstStep;
    }
  };

  const FirstStepButton = (
    <Button
      id="generate-api-key-button"
      disabled={isApiKeyNameCorrect}
      size="medium"
      variant="contained"
      color="primary"
      onClick={handleGenerateKey}
    >
      {translation.generate_key}
    </Button>
  );
  const ThirdStepButton = (
    <Button id="finish-button" size="medium" variant="contained" color="primary" onClick={handleClose}>
      {translation.finish}
    </Button>
  );

  const getStepButton = () => {
    switch (activeStep) {
      case LOADER_STEP:
        return <></>;
      case API_KEY_STEP:
        return ThirdStepButton;
      case NAME_INPUT_STEP:
      default:
        return FirstStepButton;
    }
  };
  return (
    <Box
      id="add-api-key-modal"
      style={{
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        backgroundColor: '#252525',
        border: '1px solid #444',
        borderRadius: '4px'
      }}
    >
      <ComponentWrapper width="37.5rem" flexDirection="column">
        <ComponentWrapper padding="1rem" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" color="textPrimary">
            {translation.add_api_key}
          </Typography>
          <IconButton onClick={handleClose} style={{ padding: '4px' }}>
            <CloseOutlined style={{ color: 'rgba(255, 255, 255, 0.56)' }} />
          </IconButton>
        </ComponentWrapper>
        <Divider />
        <ComponentWrapper height="17rem">
          <Stepper
            connector={<StyledStepConnector />}
            activeStep={activeStep}
            orientation="vertical"
            style={{
              height: '17rem',
              maxHeight: '17rem',
              minHeight: '17rem',
              padding: '1.5rem 1rem 1rem 1rem',
              width: '10.5rem',
              gap: '0.5rem',
              alignItems: 'stretch'
            }}
          >
            {steps.map((step) => (
              <Step key={step}>
                <StepLabel>{step}</StepLabel>
              </Step>
            ))}
          </Stepper>
          <Divider orientation="vertical" />
          <ComponentWrapper>{getStepContent()}</ComponentWrapper>
        </ComponentWrapper>
        <Divider />
        <ComponentWrapper justifyContent="end" height="4rem" padding="0.75rem 1rem">
          {getStepButton()}
        </ComponentWrapper>
      </ComponentWrapper>
    </Box>
  );
};

export default AddApiKey;
